import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { openExternalWithNotice } from '../../utils/externalLinks';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import { phoneHref } from '../../utils/phone';
import './ServicesPage.css';

const renderFormattedInstructions = (text) => {
  if (!text || !text.trim()) return null;
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {lines.map((line, idx) => {
        const parts = line.split(/:(.+)/);
        const hasTitle = parts.length >= 2 && parts[0].trim();
        return (
          <div key={idx} style={{ fontSize: '0.88rem', color: '#334155', lineHeight: '1.6', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <span style={{ color: '#2563eb', fontWeight: 'bold' }}>•</span>
            {hasTitle ? (
              <div>
                <strong style={{ color: '#0f172a', fontWeight: 700 }}>{parts[0].trim()}:</strong>{' '}
                <span>{parts[1].trim()}</span>
              </div>
            ) : (
              <span>{line}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};


const Icon = ({ name }) => {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  const paths = {
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    award: <><circle cx="12" cy="8" r="6" /><path d="M15.48 12.89 17 22l-5-3-5 3 1.52-9.11" /></>,
    home: <><path d="M3 11 12 3l9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></>,
    rupee: <><path d="M6 3h12" /><path d="M6 8h12" /><path d="M6 13h7a5 5 0 0 0 0-10" /><path d="m6 13 8 8" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h6" /></>,
    check: <><path d="M20 6 9 17l-5-5" /></>,
    heart: <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" /></>,
    arrow: <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
    card: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /><path d="M7 15h4" /></>,
    access: <><circle cx="12" cy="4" r="2" /><path d="M10 22v-7l-3-1" /><path d="M14 22v-7l3-1" /><path d="m8 7 4 2 4-2" /><path d="M12 9v6" /></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    leaf: <><path d="M11 20A7 7 0 0 1 4 13C4 5 13 3 20 3c0 7-2 16-9 16Z" /><path d="M4 20c4-7 10-9 16-17" /></>,
    grid: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.92.33 1.82.63 2.69a2 2 0 0 1-.45 2.11L8 9.8a16 16 0 0 0 6.2 6.2l1.28-1.28a2 2 0 0 1 2.11-.45c.87.3 1.77.51 2.69.63A2 2 0 0 1 22 16.92z" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  };
  return <svg {...common}>{paths[name] || paths.grid}</svg>;
};

const formatCurrency = (value, contactGp = false) => {
  if (contactGp) return 'शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा';
  const number = Number(value || 0);
  if (!number) return 'मोफत';
  return `₹ ${formatMarathiNumber(number, { maximumFractionDigits: 0 })}`;
};

const normalize = value => String(value || '').toLowerCase().trim();

const normalizeList = value => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(item => {
    if (typeof item === 'string') return item.trim();
    if (item && typeof item === 'object') return String(item.name || item.document_name || item.label || '').trim();
    return '';
  }).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeList(parsed);
    } catch {
      return value.split(/\r?\n|,/).map(item => item.trim()).filter(Boolean);
    }
  }
  return [];
};

const normalizeDocumentRequirements = value => {
  let raw = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      raw = value.split(/\r?\n|,/);
    }
  }
  if (!Array.isArray(raw)) raw = [];

  return raw.map(item => {
    if (typeof item === 'string') return { name: item.trim(), is_required: true };
    if (item && typeof item === 'object') {
      return {
        name: String(item.name || item.document_name || item.label || '').trim(),
        is_required: (item.is_required ?? item.required ?? true) !== false,
      };
    }
    return { name: '', is_required: true };
  }).filter(item => item.name);
};



const externalUrl = value => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

function buildServices(fees = []) {
  const activeFees = (fees || []).filter(fee => fee?.is_active !== false);

  return activeFees.map(fee => {
    const type = fee.service_type || 'other';
    return {
      type,
      label: fee.service_name || type.replaceAll('_', ' '),
      sourceName: fee.service_name || type.replaceAll('_', ' '),
      category: fee.display_category || 'प्रमाणपत्र',
      tone: fee.display_tone || 'blue',
      icon: fee.display_icon || '📄',
      description: fee.service_description || '',
      processingTime: fee.processing_time || '',
      documents: normalizeDocumentRequirements(fee.required_documents),
      applicationSteps: normalizeList(fee.application_steps),
      publicInstructions: fee.public_instructions || '',
      offlineFormUrl: fee.offline_form_url || '',
      externalOfficeName: fee.external_office_name || '',
      externalOfficeAddress: fee.external_office_address || '',
      externalContactPhone: fee.external_contact_phone || '',
      externalWebsiteUrl: fee.external_website_url || '',
      contactGpForFees: Boolean(fee.contact_gp_for_fees),
      feeAmount: fee.fee_amount,
      duplicateFee: fee.duplicate_fee,
      exemptions: fee.exemptions,
      isConfigured: true,
      serviceProvider: fee.service_provider || 'grampanchayat',
      isOnline: (fee.service_provider || 'grampanchayat') === 'grampanchayat',
    };
  });
}

export default function ServicesPage() {
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { tenantData, loading } = useTenant();
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [expandedType, setExpandedType] = useState(null);
  const requestedServiceType = searchParams.get('service') || searchParams.get('type');

  const tenant = tenantData?.tenant;
  const notices = tenantData?.notices || [];
  const services = useMemo(() => buildServices(tenantData?.fees || []), [tenantData]);
  const categories = useMemo(() => {
    const items = [...new Set(services.map(service => service.category).filter(Boolean))];
    return ['all', ...items];
  }, [services]);

  const filteredServices = useMemo(() => {
    const text = normalize(query);
    return services.filter(service => {
      const matchesCategory = category === 'all' || service.category === category;
      const matchesQuery = !text || normalize(`${service.label} ${service.sourceName} ${service.description} ${service.type}`).includes(text);
      return matchesCategory && matchesQuery;
    });
  }, [services, query, category]);

  useEffect(() => {
    if (!requestedServiceType || !services.length) return;

    const target = services.find(service => service.type === requestedServiceType);
    if (!target) return;

    setCategory('all');
    setQuery('');
    setExpandedType(target.type);

    window.requestAnimationFrame(() => {
      const element = document.getElementById(`service-${target.type}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.focus({ preventScroll: true });
    });
  }, [requestedServiceType, services]);

  if (loading || !tenantData) return <div className="gp-loading">माहिती लोड होत आहे...</div>;

  const freeCount = services.filter(service => Number(service.feeAmount || 0) === 0 && !service.contactGpForFees).length;
  const configuredCount = services.filter(service => service.isConfigured && service.isOnline !== false).length;
  const applyBase = isAuthenticated ? `/gp/${tenantSlug}/apply-certificate` : `/gp/${tenantSlug}/register`;
  const applyLabel = isAuthenticated ? 'ऑनलाइन अर्ज करा' : 'नोंदणी करून अर्ज करा';
  const helpPhone = tenant?.contact_phone || '+91 99234 66265';

  return (
    <div className="services-page">
      <nav className="services-breadcrumb">
        <Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link>
        <span>›</span>
        <span>सेवा</span>
      </nav>

      <section className="services-hero">
        <div className="services-hero-copy">
          <span className="services-kicker">ग्रामपंचायत नागरिक सेवा</span>
          <h1>नागरी सेवा व प्रमाणपत्रे</h1>
          <p>घरबसल्या प्रमाणपत्रासाठी अर्ज करा, शुल्क तपासा, आवश्यक कागदपत्रे पहा आणि मंजूर प्रमाणपत्र डाउनलोड करा.</p>
          <div className="services-hero-actions">
            <Link to={`${applyBase}${isAuthenticated ? '' : `?gp=${tenantSlug}`}`} className="service-primary-action">{applyLabel}</Link>
            <Link to={`/gp/${tenantSlug}/verify`} className="service-secondary-action">🔍 प्रमाणपत्र पडताळा</Link>
          </div>
        </div>
      </section>

      <section className="services-stat-grid">
        <article><span className="stat-icon blue"><Icon name="grid" /></span><p><strong>{formatMarathiNumber(services.length)}</strong><small>एकूण सेवा</small></p></article>
        <article><span className="stat-icon green"><Icon name="check" /></span><p><strong>{formatMarathiNumber(configuredCount)}</strong><small>ऑनलाइन उपलब्ध</small></p></article>
        <article><span className="stat-icon amber"><Icon name="rupee" /></span><p><strong>{formatMarathiNumber(freeCount)}</strong><small>मोफत सेवा</small></p></article>
        <article><span className="stat-icon purple"><Icon name="clock" /></span><p><strong>७-१०</strong><small>सरासरी कार्यदिवस</small></p></article>
      </section>

      <div className="services-shell">
        <aside className="services-sidebar">
          <div className="services-panel">
            <div className="panel-title-row">
              <h2>शोध व फिल्टर</h2>
              <button type="button" onClick={() => { setQuery(''); setCategory('all'); }}>रीसेट</button>
            </div>
            <label className="service-search">
              <Icon name="search" />
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="सेवा शोधा..." />
            </label>
            <div className="service-filter-list">
              {categories.map(item => (
                <button
                  key={item}
                  type="button"
                  className={category === item ? 'active' : ''}
                  onClick={() => setCategory(item)}
                >
                  <span>{item === 'all' ? 'सर्व सेवा' : item}</span>
                  <b>{formatMarathiNumber(item === 'all' ? services.length : services.filter(service => service.category === item).length)}</b>
                </button>
              ))}
            </div>
          </div>

          <div className="services-help-panel">
            <span><Icon name="phone" /></span>
            <h3>मदत हवी आहे?</h3>
            <p>अर्ज भरताना अडचण आल्यास ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
            <a href={phoneHref(helpPhone)}>संपर्क साधा</a>
          </div>

          <div className="services-panel service-notice-panel" style={{ marginTop: 14 }}>
            <div className="panel-title-row">
              <h2>महत्त्वाच्या सूचना</h2>
              <Link to={`/gp/${tenantSlug}/notices`}>सर्व</Link>
            </div>
            {notices?.length ? (
              <div className="service-notice-scroll" aria-label="महत्त्वाच्या सूचना">
                <div className="service-notice-track">
                  {[0, 1].map(copy => (
                    <div className="service-notice-group" key={copy} aria-hidden={copy === 1}>
                      {(notices || []).slice(0, 5).map(notice => (
                        <Link key={`${copy}-${notice.id}`} to={`/gp/${tenantSlug}/notices`}>
                          <span />{notice.title}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : <p><span></span>अर्ज करताना सर्व कागदपत्रे स्पष्ट अपलोड करा.</p>}
          </div>
        </aside>

        <main className="services-main">
          <div className="services-section-head">
            <div>
              <span>नागरिकांसाठी उपलब्ध सेवा</span>
              <h2>सर्व सेवा</h2>
            </div>
            <p>{formatMarathiNumber(filteredServices.length)} सेवा दर्शविल्या आहेत</p>
          </div>

          <div className="services-card-grid">
            {filteredServices.map(service => {
              const isExpanded = expandedType === service.type;
              const canApplyOnline = service.isOnline !== false;
              const serviceApplyTarget = canApplyOnline
                ? `${applyBase}${isAuthenticated ? `?type=${service.type}` : `?service=${service.type}`}`
                : `/gp/${tenantSlug}/contact`;
              const websiteUrl = externalUrl(service.externalWebsiteUrl);
              return (
                <article
                  key={service.type}
                  id={`service-${service.type}`}
                  tabIndex={-1}
                  className={`service-card ${service.tone}${isExpanded ? ' expanded' : ''}`}
                >
                  <div className="service-card-top">
                    <span className="service-card-icon" aria-hidden="true">{service.icon}</span>
                    <div className="service-badges-group">
                      <span className="service-category">{service.category}</span>
                      {canApplyOnline ? (
                        <span className="service-online-badge">🟢 ऑनलाइन</span>
                      ) : (
                        <span className="service-offline-badge">🏛️ मार्गदर्शक</span>
                      )}
                    </div>
                  </div>
                  <h3>{service.label}</h3>
                  <p>{service.description}</p>
                  <div className="service-card-meta">
                    <span><Icon name="rupee" /> {formatCurrency(service.feeAmount, service.contactGpForFees)}</span>
                    <span><Icon name="clock" /> {toMarathiDigits(service.processingTime)}</span>
                  </div>
                  <div className="service-card-actions">
                    {canApplyOnline ? (
                      <Link to={serviceApplyTarget}>{applyLabel}</Link>
                    ) : (
                      <button type="button" className="service-primary-inline" onClick={() => setExpandedType(service.type)}>
                        अर्ज सूचना पहा
                      </button>
                    )}
                    <button type="button" onClick={() => setExpandedType(current => current === service.type ? null : service.type)}>
                      {isExpanded ? 'तपशील लपवा' : 'तपशील'}
                    </button>
                    {websiteUrl && (
                      <a className="service-website-action" href={websiteUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, websiteUrl)}>
                        ऑनलाइन लिंक / वेबसाइट
                      </a>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="service-card-detail">
                      <div className="service-detail-grid">
                        <div>
                          <h3>आवश्यक कागदपत्रे</h3>
                          {service.documents.length > 0 ? (
                            <ul className="document-check-list">
                              {service.documents.map(item => (
                                <li key={item.name}>
                                  {item.name}{item.is_required === false ? ' (Optional)' : ''}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="service-panel-empty">या सेवेसाठी कागदपत्रे कॉन्फिगर केलेली नाहीत.</p>
                          )}
                        </div>
                        {websiteUrl && (
                          <div className="service-website-box">
                            <h3>ऑनलाइन लिंक / वेबसाइट</h3>
                            <p>या सेवेबद्दल अधिक माहिती किंवा बाहेरील ऑनलाइन अर्जासाठी खालील लिंक वापरा.</p>
                            <a href={websiteUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, websiteUrl)}>{service.externalWebsiteUrl}</a>
                          </div>
                        )}
                        {!canApplyOnline && (
                          <div className="service-guidance-box">
                            <h3>अर्ज कुठे व कसा करावा</h3>
                            {(service.externalOfficeName || service.externalOfficeAddress || service.externalContactPhone || service.externalWebsiteUrl) && (
                              <dl>
                                {service.externalOfficeName && <div><dt>कार्यालय</dt><dd>{service.externalOfficeName}</dd></div>}
                                {service.externalOfficeAddress && <div><dt>पत्ता</dt><dd>{service.externalOfficeAddress}</dd></div>}
                                {service.externalContactPhone && <div><dt>फोन</dt><dd><a href={phoneHref(service.externalContactPhone)}>{service.externalContactPhone}</a></dd></div>}
                                {websiteUrl && <div><dt>वेबसाइट</dt><dd><a href={websiteUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, websiteUrl)}>ऑनलाइन माहिती पहा</a></dd></div>}
                              </dl>
                            )}
                            {service.applicationSteps?.length > 0 && (
                              <ol>
                                {service.applicationSteps.map(step => <li key={step}>{step}</li>)}
                              </ol>
                            )}
                            {service.publicInstructions && renderFormattedInstructions(service.publicInstructions)}
                            {!service.applicationSteps?.length && !service.publicInstructions && (
                              <p>वरील कागदपत्रांसह संबंधित कार्यालयात अर्ज सादर करा. अधिक माहितीसाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
                            )}
                          </div>
                        )}
                        <div className="service-fee-box">
                          <h3>शुल्क तपशील</h3>
                          <dl>
                            <div><dt>मूळ शुल्क</dt><dd>{formatCurrency(service.feeAmount, service.contactGpForFees)}</dd></div>
                            <div><dt>डुप्लिकेट प्रत</dt><dd>{service.contactGpForFees ? 'शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा' : (service.duplicateFee ? formatCurrency(service.duplicateFee) : 'लागू असल्यास')}</dd></div>
                            <div><dt>कालावधी</dt><dd>{service.processingTime}</dd></div>
                          </dl>
                          {canApplyOnline ? (
                            <Link to={serviceApplyTarget}>{applyLabel}</Link>
                          ) : (
                            websiteUrl ? (
                              <a href={websiteUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, websiteUrl)}>अधिकृत लिंक उघडा</a>
                            ) : (
                              <Link to={`/gp/${tenantSlug}/contact`}>मदतीसाठी संपर्क साधा</Link>
                            )
                          )}
                        </div>
                        {canApplyOnline && service.publicInstructions && (
                          <div className="service-guidance-box" style={{ gridColumn: '1 / -1', marginTop: 12 }}>
                            <h3>📜 मार्गदर्शक सूचना व अटी</h3>
                            {renderFormattedInstructions(service.publicInstructions)}
                          </div>
                        )}
                        {service.offlineFormUrl && (
                          <div className="service-offline-pdf-box" style={{ gridColumn: '1 / -1', marginTop: 12, background: '#f0fdf4', border: '1px dashed #86efac', padding: '14px 18px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontSize: '1.6rem' }}>📄</span>
                              <div>
                                <strong style={{ color: '#166534', fontSize: '0.95rem', display: 'block' }}>ब्लँक ऑफलाईन अर्ज फॉर्म (Blank Offline Form PDF)</strong>
                                <span style={{ color: '#15803d', fontSize: '0.82rem' }}>ऑनलाइन / स्वहस्ते अर्ज सादर करण्यापूर्वी हा ब्लँक ऑफलाईन अर्ज फॉर्म डाऊनलोड करून तयार ठेवू शकता.</span>
                              </div>
                            </div>
                            <a
                              href={service.offlineFormUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#16a34a', color: '#ffffff', padding: '8px 18px', borderRadius: 8, fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)' }}
                            >
                              📥 ब्लँक फॉर्म PDF डाऊनलोड करा
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {!filteredServices.length && (
            <div className="services-empty">
              <h3>सेवा सापडली नाही</h3>
              <p>शोध शब्द बदला किंवा सर्व सेवा पाहण्यासाठी फिल्टर रीसेट करा.</p>
            </div>
          )}

        </main>


      </div>
    </div>
  );
}
